import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, Search, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { fetchSports } from "@/lib/sportsCatalog";
import PlacementListItem from "@/components/placements/PlacementListItem";
import PlacementDetail from "@/components/placements/PlacementDetail";
import { useLang, translateFr as tfr } from "@/i18n";

const TABS = [
  { id: "active", label: "Actifs" },
  { id: "watch", label: tfr("À surveiller") },
  { id: "history", label: "Historique" },
];

export default function Placements() {
  const { tf } = useLang();
  const navigate = useNavigate();
  const { placementId } = useParams();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ active: 0, watch: 0, history: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [sport, setSport] = useState("__all__");
  const [followState, setFollowState] = useState("__all__");
  const [sports, setSports] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchSports().then(setSports).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ tab, q, sport, follow_state: followState });
      const r = await api.get(`/placements?${params}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch {
      toast.error(tf("Impossible de charger les placements"));
    } finally { setLoading(false); }
  }, [tab, q, sport, followState, tf]);

  useEffect(() => { load(); }, [load]);

  // deep-link from athlete profile: /placements?athlete=:pid
  const athleteParam = searchParams.get("athlete");
  useEffect(() => {
    if (!athleteParam || placementId) return;
    api.get(`/placements?player_id=${athleteParam}`).then((r) => {
      const first = (r.data.items || [])[0];
      if (first) navigate(`/placements/${first.id}`, { replace: true });
    }).catch(() => {});
  }, [athleteParam, placementId, navigate]);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const r = await api.get(`/placements/${id}`);
      setDetail(r.data);
    } catch {
      toast.error(tf("Placement introuvable"));
      navigate("/placements", { replace: true });
    } finally { setDetailLoading(false); }
  }, [navigate, tf]);

  useEffect(() => {
    if (placementId) loadDetail(placementId);
    else setDetail(null);
  }, [placementId, loadDetail]);

  // desktop auto-select first item
  useEffect(() => {
    if (!placementId && !athleteParam && items.length > 0 && window.innerWidth >= 1024) {
      navigate(`/placements/${items[0].id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, placementId]);

  const refreshAll = useCallback(() => {
    load();
    if (placementId) loadDetail(placementId);
  }, [load, loadDetail, placementId]);

  const exportCsv = async () => {
    if (exporting || items.length === 0) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ tab, q, sport, follow_state: followState });
      const r = await api.get(`/placements/export.csv?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement("a");
      el.href = url;
      el.download = `athlynx-placements-${new Date().toISOString().slice(0, 10)}.csv`;
      el.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(tf("Export impossible"));
    } finally { setExporting(false); }
  };

  const sportsUsed = useMemo(() => {
    const ids = new Set(items.map((i) => i.athlete?.sport).filter(Boolean));
    return sports.filter((s) => ids.has(s.id));
  }, [sports, items]);

  const showDetailMobile = !!placementId;

  return (
    <div className="px-4 md:px-6 lg:px-8 py-5 max-w-[1500px] mx-auto animate-fade-in" data-testid="placements-page">
      {/* Breadcrumb + header */}
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid="placements-back-dashboard">
        <ChevronLeft className="h-3.5 w-3.5" /> {tf("Dashboard")} <span className="text-muted-foreground/50">›</span> <span className="text-foreground/70">{tf("Placements")}</span>
      </Link>
      <div className="flex items-start justify-between gap-3 mt-1.5 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-[34px] font-bold tracking-tight" data-testid="placements-title">{tf("Placements")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{tf("Suivez l'intégration de vos athlètes, leurs performances et les prochaines étapes de leur carrière.")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={exporting || items.length === 0}
            className="h-10 gap-1.5 border-border dark:border-white/10" data-testid="placements-export-btn">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">{tf("Exporter")}</span>
          </Button>
        </div>
      </div>

      {/* Tabs + filters */}
      <div className={cn("mt-4 space-y-3", showDetailMobile && "hidden lg:block")}>
        <div className="flex gap-1.5 overflow-x-auto" data-testid="placements-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`placements-tab-${t.id}`}
              className={cn("shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium inline-flex items-center gap-2 transition-colors",
                tab === t.id ? "border-foreground/25 bg-secondary text-foreground" : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60")}>
              {tf(t.label)}
              <span className="tabular-nums rounded-full border border-border px-1.5 py-px text-[11px]">
                {counts[t.id] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tf("Rechercher un athlète, un club, une compétition…")}
              className="pl-9 h-10 rounded-xl border-border bg-card text-sm" data-testid="placements-search" />
          </div>
          <div className="flex gap-2">
            <Select value={sport} onValueChange={setSport}>
              <SelectTrigger className="h-10 w-[150px] rounded-xl border-border bg-card" data-testid="placements-sport-filter">
                <SelectValue placeholder={tf("Tous les sports")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{tf("Tous les sports")}</SelectItem>
                {sportsUsed.map((s) => <SelectItem key={s.id} value={s.id}>{tf(s.label)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={followState} onValueChange={setFollowState}>
              <SelectTrigger className="h-10 w-[160px] rounded-xl border-border bg-card" data-testid="placements-state-filter">
                <SelectValue placeholder={tf("Statut de suivi")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{tf("Statut de suivi")}</SelectItem>
                <SelectItem value="NORMAL">{tf("Suivi normal")}</SelectItem>
                <SelectItem value="ATTENTION">{tf("Attention")}</SelectItem>
                <SelectItem value="ACTION_REQUIRED">{tf("Action requise")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Master / detail */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(280px,0.35fr)_minmax(0,1fr)] gap-4 items-start">
        {/* List */}
        <div className={cn("space-y-2 min-w-0", showDetailMobile && "hidden lg:block")} data-testid="placements-list">
          {loading ? (
            [0, 1, 2].map((i) => <div key={i} className="h-[104px] rounded-2xl bg-secondary/60 animate-pulse" />)
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card px-5 py-10 text-center" data-testid="placements-empty">
              <p className="text-sm font-medium">{tf("Aucun placement dans cette vue.")}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {tab === "active"
                  ? tf("Les athlètes apparaîtront ici après confirmation de leur placement depuis la Pipeline.")
                  : tf("Les placements correspondants apparaîtront ici.")}
              </p>
              {tab === "active" && (
                <Button asChild variant="outline" size="sm" className="mt-4 h-9 border-border dark:border-white/10">
                  <Link to="/pipeline" data-testid="placements-empty-pipeline-cta">{tf("Voir la Pipeline")}</Link>
                </Button>
              )}
            </div>
          ) : (
            items.map((it) => (
              <PlacementListItem key={it.id} item={it} selected={it.id === placementId}
                onClick={() => navigate(`/placements/${it.id}`)} />
            ))
          )}
        </div>

        {/* Detail */}
        <div className={cn("min-w-0", !showDetailMobile && "hidden lg:block")}>
          {showDetailMobile && (
            <button onClick={() => navigate("/placements")}
              className="lg:hidden inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3"
              data-testid="placement-back-list">
              <ChevronLeft className="h-3.5 w-3.5" /> {tf("Placements")}
            </button>
          )}
          {detailLoading ? (
            <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
              <div className="h-14 w-2/3 rounded-xl bg-secondary/60 animate-pulse" />
              <div className="h-24 rounded-xl bg-secondary/60 animate-pulse" />
              <div className="h-64 rounded-xl bg-secondary/60 animate-pulse" />
            </div>
          ) : detail ? (
            <PlacementDetail detail={detail} onRefresh={refreshAll} />
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-card/50 px-6 py-16 text-center text-sm text-muted-foreground"
              data-testid="placement-detail-empty">
              {tf("Sélectionnez un athlète placé pour ouvrir son espace de suivi.")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
